import React, { useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import {
  Col,
  Row,
  Spin,
  Form,
  Button,
  message,
} from 'antd';
import {
  EyeOutlined,
  FileDoneOutlined,
  PlusCircleOutlined,
  // EyeOutlined,
} from '@ant-design/icons';
import currencyFormatter from 'utils/toCurrency';
import Previewer from 'components/Previewer';
import CustomTable from 'components/Table';
import InputSearch from 'components/InputSearch';
import { onError } from 'utils/handlers';
import ModalFacturacion from 'views/Recaudacion/Caja/ModalFacturacion';
import BusquedaRecibo from 'views/CatalogosDeRecaudacion/Recibo/BusquedaRecibo';
import {
  getRecibo, getRecibos, getRecibosPDF,
} from 'api/recaudacion/recibos';
import { sendPDFToEmail } from 'api/configuracion';
import { ESTADOS_GLOBALES_MAP } from 'utils/estadosGlobales';

// eslint-disable-next-line react/prop-types
const Facturacion = ({ permission }) => {
  const timeoutRecibos = useRef();
  const collapsedSidebar = useSelector(({ app }) => app.collapsedSidebar);
  const estadosGlobales = useSelector(({ catalogs }) => catalogs.estadosGlobales);
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [visibleBusqueda, setVisibleBusqueda] = useState(false);
  const [visiblePreviewPDF, setVisiblePreviewPDF] = useState(false);
  const [data, setData] = useState({ results: [] });
  const [base64, setBase64] = useState();
  const [recibosAFacturar, setReciboAFacturar] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [visibleModal, setVisibleModal] = useState(false);
  const [params, setParams] = useState(null);

  const onFinish = async () => {
    try {
      clearTimeout(timeoutRecibos?.current);
      setLoading(true);
      await form.validateFields();
      const { folio } = form.getFieldsValue();
      const _data = await getRecibos({ folio });
      if (_data.count) {
        setData(_data);
      } else {
        message.info('Recibo no encontrado.');
        setData({ results: [] });
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const setTimeoutRecibos = (folio, ms) => {
    if (folio) {
      clearTimeout(timeoutRecibos.current);
      timeoutRecibos.current = setTimeout(() => onFinish(), ms);
    } else {
      clearTimeout(timeoutRecibos.current);
    }
  };

  const onValuesChange = (changed) => {
    const { folio } = changed;
    const changedProp = Object.keys(changed)?.shift();
    if (changedProp === 'folio') {
      setTimeoutRecibos(folio, 2000);
    }
  };

  const onFinishBusqueda = (_data, _params) => {
    setData(_data);
    setParams(_params);
    setVisibleBusqueda(false);
    setSelectedRowKeys([]);
  };

  const columnsRecibo = [
    {
      titleText: 'Folio',
      dataIndex: 'folio',
      key: 'folio',
      width: 150,
      render: (val, record) => val || record.id,
    },
    {
      titleText: 'Folio de Facturación',
      dataIndex: 'folio_de_facturacion',
      key: 'folio_de_facturacion',
      width: 220,
    },
    {
      titleText: 'Facturado',
      dataIndex: 'xml_archivo',
      key: 'xml_archivo',
      width: 100,
      render: (val) => (val ? 'Si' : 'No'),
    },
    {
      titleText: 'Importe',
      dataIndex: 'importe_total',
      key: 'importe_total',
      width: 150,
      render: (val) => currencyFormatter(val),
    },
    {
      titleText: 'Corte',
      dataIndex: 'corte',
      key: 'corte',
      width: 150,
      render: (val) => val?.descripcion,
    },
    {
      titleText: 'Canal de pago',
      dataIndex: 'canal_de_pago',
      key: 'canal_de_pago',
      width: 250,
      render: (val) => val?.descripcion,
    },
    {
      titleText: 'Estado',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      width: 150,
      render: (val) => estadosGlobales.find((e) => e.id === val)?.descripcion,
    },
  ];

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onClickPreview = async () => {
    setLoading(true);
    const result = await getRecibosPDF(selectedRowKeys);
    if (result.success) {
      setBase64(result.base64);
      setVisiblePreviewPDF(true);
    }
    setLoading(false);
  };

  const onClickXML = async () => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const _recibo = data.results.find((e) => e.id === key);

    if (!_recibo.xml_archivo) {
      message.warn('El recibo seleccionado aún no ha sido facturado. Verifica y vuelve a intentarlo.');
      setLoading(false);
      return;
    }

    const oldURL = _recibo.xml_archivo;
    // const baseURL = 'https://demosigob.obs.la-north-2.myhuaweicloud.com/demosigob/media/';
    // eslint-disable-next-line max-len
    // const updatedURL = oldURL.replace(/^http:\/\/obsdemos\.migob\.digital\/demosigob\/media\//, baseURL);
    const baseURL = 'https://mun-oaxaca.obs.la-north-2.myhuaweicloud.com/mun-oaxaca/media/';
    const updatedURL = oldURL.replace(/^http:\/\/obs-mun-oaxaca\.migob\.digital\/mun-oaxaca\/media\//, baseURL);
    const croppedURL = `${updatedURL.split('.xml')[0]}.xml`;
    window.open(croppedURL);
    setLoading(false);
  };

  const onClickFacturar = () => {
    const [key] = selectedRowKeys;
    const _recibo = data.results.find((e) => e.id === key);

    if (_recibo.xml_archivo) {
      message.warn('Este recibo ya ha sido facturado. No es posible generar una nueva factura.');
      return;
    }

    if (_recibo.estados_globales === ESTADOS_GLOBALES_MAP.CANCELADO) {
      message.warn('No se puede facturar este recibo porque ha sido cancelado.');
      return;
    }

    setReciboAFacturar([_recibo.id]);
    setVisibleModal(true);
  };

  const sendToEmail = async (values) => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const _recibo = await getRecibo(key);
    const idRecibo = [_recibo?.id];
    const oldURL = _recibo?.xml_archivo;
    // const baseURL = 'https://demosigob.obs.la-north-2.myhuaweicloud.com/demosigob/media/';
    // eslint-disable-next-line max-len
    // const updatedURL = oldURL.replace(/^http:\/\/obsdemos\.migob\.digital\/demosigob\/media\//, baseURL);
    const baseURL = 'https://mun-oaxaca.obs.la-north-2.myhuaweicloud.com/mun-oaxaca/media/';
    const updatedURL = oldURL.replace(/^http:\/\/obs-mun-oaxaca\.migob\.digital\/mun-oaxaca\/media\//, baseURL);
    const url_xml = `${updatedURL.split('.xml')[0]}.xml`;
    const pdfs = await getRecibosPDF(idRecibo);
    if (pdfs.success) {
      const archivo_base64 = pdfs.base64.replace('data:application/pdf;base64,', '');
      await sendPDFToEmail({
        archivo_base64,
        email: values.email,
        reporte_redaccion: 'Comprobante de Pago',
        nombre_del_archivo: 'Comprobante de Pago',
        url_xml,
      });
    }

    setLoading(false);
  };

  const rules = {
    folio: [{ required: true }],
  };

  return (
    <Row align="center" justify="center" className="container">
      <Spin
        tip="Cargando..."
        spinning={loading}
      >
        <>
          <Form
            layout="vertical"
            form={form}
            scrollToFirstError
            onFinish={() => onFinish()}
            onValuesChange={onValuesChange}
          >
            <Row gutter={10}>
              <Col xs={24} sm={24} md={collapsedSidebar ? 12 : 16} lg={8}>
                <InputSearch
                  name="folio"
                  label="Folio"
                  rules={rules.folio}
                  onPressEnter={() => {
                    clearTimeout(timeoutRecibos.current);
                    onFinish();
                  }}
                  onChange={({ target: { value } }) => form.setFieldsValue({ folio: value })}
                  onClickAdvanced={() => {
                    setVisibleBusqueda(true);
                  }}
                />
              </Col>
              <Col span={0}>
                <Form.Item>
                  <Button htmlType="submit" />
                </Form.Item>
              </Col>
            </Row>
          </Form>
          <CustomTable
            cols={columnsRecibo}
            data={data?.results}
            rowSelection={rowSelection}
            currentPage={currentPage}
            pagination={false}
            paginationCallback={async (page = 1) => {
              setLoading(true);
              setCurrentPage(page);
              const recibos = await getRecibos({ page, ...params });
              setData(recibos);
              setLoading(false);
            }}
            count={data?.count}
            handleOnRowClick={handleOnRowClick}
            allowSearch={false}
            allowSort={false}
            allowDelete={false}
            allowEdit={false}
            rowKey="id"
            permission={permission}
            hasAllViewPermission
            customActions={[
              {
                onClick: onClickFacturar,
                icon: <PlusCircleOutlined />,
                text: 'Facturar',
                disabledBySelection: true,
              },
              {
                onClick: onClickXML,
                icon: <FileDoneOutlined />,
                text: 'Obtener XML',
                disabled: !selectedRowKeys.length,
              },

              {
                onClick: onClickPreview,
                icon: <EyeOutlined />,
                text: 'Ver comprobante de pago',
              },
            ]}
          />
        </>
      </Spin>
      <Previewer
        visible={visiblePreviewPDF}
        allowSend
        sendToEmail
        // cuentaUnicaID={cuentaUnicaID}
        base64={base64}
        setVisible={() => {
          setVisiblePreviewPDF(false);
          setBase64();
        }}
      />
      <ModalFacturacion
        visible={visibleModal}
        setVisible={setVisibleModal}
        recibosAFacturar={recibosAFacturar}
        callback={async (values) => {
          setLoading(true);
          const recibos = await getRecibos({ ...params });
          setData(recibos);
          setLoading(false);
          if (recibos) {
            await sendToEmail(values);
          }
        }}
      />
      <BusquedaRecibo
        onFinishBusqueda={onFinishBusqueda}
        visible={visibleBusqueda}
        setVisible={setVisibleBusqueda}
      />
    </Row>
  );
};

export default Facturacion;
